import { styled } from "goober";
import { useId, useMemo } from "react";
import { smoothPath, type Point } from "../lib/smoothPath";
import { Text } from "./Text";

// ── Graph constants (matching ExecutionGraph.tsx) ───────────────────────────

const VIEW_WIDTH = 960;
const VIEW_HEIGHT = 280;
const PADDING_X = 24;
const PADDING_TOP = 32;
const PADDING_BOTTOM = 4;
const GRAPH_WIDTH = VIEW_WIDTH - PADDING_X * 2;
const GRAPH_HEIGHT = VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
const BASELINE_Y = PADDING_TOP + GRAPH_HEIGHT;

const BUCKET_COUNT = 24;
const NOW_FRACTION = 0.75; // "now" sits 3/4 across, matching ExecutionGraph
const NOW_X = PADDING_X + NOW_FRACTION * GRAPH_WIDTH;

// Simple seeded PRNG (mulberry32)
function mulberry32(seed: number) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return h;
}

function generateFakeData(seed: string): number[] {
  const rng = mulberry32(hashSeed(seed));
  const data: number[] = [];
  for (let i = 0; i < BUCKET_COUNT; i++) {
    data.push(rng() * 12);
  }
  // Taper the edges to zero
  data[0] = data[0]! * 0.1;
  data[1] = data[1]! * 0.4;
  data[BUCKET_COUNT - 1] = data[BUCKET_COUNT - 1]! * 0.1;
  data[BUCKET_COUNT - 2] = data[BUCKET_COUNT - 2]! * 0.4;
  return data;
}

function buildGhostCurve(seed: string): { curve: string; area: string } {
  const data = generateFakeData(seed);
  const maxCount = Math.max(...data);
  const dataWidth = NOW_FRACTION * GRAPH_WIDTH;
  const points: Point[] = [];
  for (let i = 0; i < BUCKET_COUNT; i++) {
    points.push({
      x: PADDING_X + (i / (BUCKET_COUNT - 1)) * dataWidth,
      y: PADDING_TOP + GRAPH_HEIGHT - (data[i]! / maxCount) * GRAPH_HEIGHT,
    });
  }
  const ext: Point[] = [{ x: 0, y: points[0]!.y }, ...points];
  const curve = smoothPath(ext);
  const area = `${curve}L${ext[ext.length - 1]!.x},${BASELINE_Y}L${ext[0]!.x},${BASELINE_Y}Z`;
  return { curve, area };
}

// ── Styled components ──────────────────────────────────────────────────────

const GraphWrapper = styled("div")`
  position: absolute;
  left: 32px;
  right: 32px;
  bottom: calc(34px + 20px + 22px);
  top: 0;
  display: flex;
  flex-direction: column;
  pointer-events: none;
`;

const GraphHeaderRow = styled("div")`
  z-index: 1;
  margin-top: 32px;
  margin-bottom: auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  opacity: 0.4;
`;

const GraphStatsRow = styled("div")`
  display: flex;
  gap: 32px;
`;

const GraphStatItem = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 76px;
`;

const GraphBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px 1px 5px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 500;
  line-height: 14px;
  width: fit-content;
  color: var(--text-secondary);
  background: var(--alpha8);
`;

const GraphBadgeDot = styled("span")`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--alpha8);
`;

const GhostedRadarContainer = styled("div")`
  position: absolute;
  top: 32px;
  right: 0;
  width: 80px;
  height: 80px;
  z-index: 1;
`;

function GhostedRadar() {
  return (
    <GhostedRadarContainer>
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle
          cx="40"
          cy="40"
          r="39"
          fill="none"
          stroke="var(--alpha8)"
          strokeWidth="1"
        />
        <circle
          cx="40"
          cy="40"
          r="19.5"
          fill="none"
          stroke="var(--alpha8)"
          strokeWidth="1"
        />
        <circle cx="40" cy="40" r="3" fill="var(--alpha8)" />
      </svg>
    </GhostedRadarContainer>
  );
}

const GraphArea = styled("div")`
  position: relative;
  flex: 1;
  min-height: 0;
  max-height: 50%;
`;

const GraphEdgeLabel = styled("div")`
  position: absolute;
  bottom: -44px;
  transform: translateX(-50%);
  color: var(--text-secondary);
  opacity: 0.4;
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 350;
  line-height: 12px;
  border-radius: 999px;
  white-space: nowrap;
`;

const GraphNowIndicator = styled("div")`
  position: absolute;
  bottom: -20px;
  top: 0;
  width: 2px;
  transform: translateX(-50%);
  background: var(--alpha8);
  pointer-events: none;
`;

const GraphNowLabel = styled("div")`
  position: absolute;
  bottom: -44px;
  transform: translateX(-50%);
  color: var(--text-secondary);
  opacity: 0.4;
  background: var(--alpha8);
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 350;
  line-height: 12px;
  border-radius: 999px;
  pointer-events: none;
  white-space: nowrap;
`;

// ── Ghosted execution graph ────────────────────────────────────────────────

export function GhostedGraph({ seed }: { seed: string }) {
  const { curve, area } = useMemo(() => buildGhostCurve(seed), [seed]);
  const uid = useId();
  const gradId = `${uid}-grad`;
  const fadeGradId = `${uid}-fade`;
  const fadeMaskId = `${uid}-mask`;

  return (
    <GraphWrapper>
      <GraphHeaderRow>
        <GraphStatsRow>
          <GraphStatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Monitor
            </Text>
            <GraphBadge>
              <GraphBadgeDot />
              Idle
            </GraphBadge>
          </GraphStatItem>
          <GraphStatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Last run
            </Text>
            <Text variant="text-regular-md" color="var(--text-secondary)">
              –
            </Text>
          </GraphStatItem>
          <GraphStatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Duration
            </Text>
            <Text variant="text-regular-md" color="var(--text-secondary)">
              –
            </Text>
          </GraphStatItem>
          <GraphStatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Status
            </Text>
            <Text variant="text-regular-md" color="var(--text-secondary)">
              –
            </Text>
          </GraphStatItem>
        </GraphStatsRow>
      </GraphHeaderRow>

      <GhostedRadar />

      <GraphArea>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--gray-400)"
                stopOpacity="0.08"
              />
              <stop offset="60%" stopColor="var(--gray-400)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={fadeGradId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop
                offset={`${(PADDING_X / VIEW_WIDTH) * 100}%`}
                stopColor="white"
                stopOpacity="1"
              />
              <stop
                offset={`${((VIEW_WIDTH - PADDING_X) / VIEW_WIDTH) * 100}%`}
                stopColor="white"
                stopOpacity="1"
              />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask
              id={fadeMaskId}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={VIEW_WIDTH}
              height={VIEW_HEIGHT}
            >
              <rect
                x="0"
                y="0"
                width={VIEW_WIDTH}
                height={VIEW_HEIGHT}
                fill={`url(#${fadeGradId})`}
              />
            </mask>
          </defs>

          <clipPath id={`${uid}-data`}>
            <rect x="0" y="0" width={NOW_X + 1} height={VIEW_HEIGHT} />
          </clipPath>

          <g mask={`url(#${fadeMaskId})`} clipPath={`url(#${uid}-data)`}>
            <path d={area} fill={`url(#${gradId})`} />
            <path
              d={curve}
              fill="none"
              stroke="var(--alpha8)"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </g>

          {/* Dashed future baseline (fades at right edge) */}
          <g mask={`url(#${fadeMaskId})`}>
            <line
              x1={NOW_X}
              y1={BASELINE_Y}
              x2={VIEW_WIDTH + PADDING_X}
              y2={BASELINE_Y}
              stroke="var(--gray-400)"
              strokeWidth="2"
              strokeDasharray="6 4"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        </svg>

        <GraphEdgeLabel style={{ left: `${(PADDING_X / VIEW_WIDTH) * 100}%` }}>
          3h ago
        </GraphEdgeLabel>
        <GraphNowIndicator style={{ left: `${(NOW_X / VIEW_WIDTH) * 100}%` }} />
        <GraphNowLabel style={{ left: `${(NOW_X / VIEW_WIDTH) * 100}%` }}>
          Now
        </GraphNowLabel>
      </GraphArea>
    </GraphWrapper>
  );
}
